/**
 * "Monthly statement" on the Reports page (operator Reports (c), migration 0256): a PDF of one
 * period, with the totals per site and well, per hauler, then the ticket list, and optionally
 * each run ticket as its own page. Made on a click; the API returns a short-lived link, which
 * opens in a new tab (as the ticket's Download PDF).
 *
 * The period is local calendar days in the browser's zone, sent with the zone. The site list is
 * Volumes "by site" for the chosen period: the sites that have tickets in it.
 */

import React from 'react';
import { ApiError } from '@lodex-pro/web-shared';
import { usePortalStatement, usePortalVolumes } from '../../hooks/useOperatorPortal';

export const STATEMENT_TOO_MANY_TICKETS_MESSAGE =
  'That period has too many tickets for one statement. Choose a shorter period or one site.';
export const STATEMENT_TOO_MANY_PAGES_MESSAGE =
  'Too many run tickets to add as pages. Turn off “Include run tickets”, or choose a shorter period or one site.';
export const STATEMENT_UNAVAILABLE_MESSAGE =
  'The statement can’t be made right now. Try again in a few minutes.';
export const STATEMENT_RATE_LIMITED_MESSAGE =
  'Too many statements in a minute. Wait a moment and try again.';
export const STATEMENT_FAILED_MESSAGE = 'Couldn’t make the statement. Try again.';

const CARD: React.CSSProperties = {
  padding: '16px',
  background: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  marginBottom: '16px',
};
const LABEL: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontSize: '13px',
};
const INPUT: React.CSSProperties = {
  padding: '6px 8px',
  borderRadius: '10px',
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  fontSize: '13px',
};
const BUTTON: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  fontSize: '13px',
  cursor: 'pointer',
};

const pad = (n: number): string => String(n).padStart(2, '0');
const day = (d: Date): string =>
  `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** The first and last day of the month `back` months before `now`'s, in local time. */
export function monthRange(now: Date, back: number): { from: string; to: string; label: string } {
  const first = new Date(now.getFullYear(), now.getMonth() - back, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  return {
    from: day(first),
    to: day(last),
    label: first.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
  };
}

/** Local midnight of `from` to the last millisecond of `to`, as the Volumes query takes them. */
function instants(from: string, to: string): { from: string; to: string } {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  return {
    from: new Date(fy!, fm! - 1, fd).toISOString(),
    to: new Date(new Date(ty!, tm! - 1, td! + 1).getTime() - 1).toISOString(),
  };
}

export function statementErrorMessage(err: Error): string {
  if (err instanceof ApiError && err.code === 'OPERATOR_REPORT_TOO_LARGE') {
    return err.fields[0]?.path === 'runTickets'
      ? STATEMENT_TOO_MANY_PAGES_MESSAGE
      : STATEMENT_TOO_MANY_TICKETS_MESSAGE;
  }
  if (err instanceof ApiError && err.code === 'OPERATOR_STATEMENT_UNAVAILABLE') {
    return STATEMENT_UNAVAILABLE_MESSAGE;
  }
  if (err instanceof ApiError && err.code === 'RATE_LIMIT_EXCEEDED') {
    return STATEMENT_RATE_LIMITED_MESSAGE;
  }
  return STATEMENT_FAILED_MESSAGE;
}

const MONTHS_LISTED = 12;
const CUSTOM = 'custom';

export function StatementCard({ now = new Date() }: { now?: Date }): React.JSX.Element {
  const months = React.useMemo(
    () => Array.from({ length: MONTHS_LISTED }, (_, i) => monthRange(now, i + 1)),
    [now],
  );
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [period, setPeriod] = React.useState<string>('0');
  const [customFrom, setCustomFrom] = React.useState<string>(months[0]!.from);
  const [customTo, setCustomTo] = React.useState<string>(months[0]!.to);
  const [siteId, setSiteId] = React.useState<string>('');
  const [runTickets, setRunTickets] = React.useState<boolean>(false);

  const chosen =
    period === CUSTOM ? { from: customFrom, to: customTo } : (months[Number(period)] ?? months[0]!);
  const rangeOk =
    /^\d{4}-\d{2}-\d{2}$/.test(chosen.from) &&
    /^\d{4}-\d{2}-\d{2}$/.test(chosen.to) &&
    chosen.from <= chosen.to;

  const sites = usePortalVolumes(
    rangeOk ? { groupBy: 'site', ...instants(chosen.from, chosen.to) } : { groupBy: 'site' },
  );
  const siteOptions = (sites.data?.groups ?? []).filter((g) => g.key !== '(none)');
  const statement = usePortalStatement();

  return (
    <section aria-label="Monthly statement" style={CARD}>
      <h2 style={{ fontSize: '16px', marginTop: 0, marginBottom: '4px' }}>Monthly statement</h2>
      <p style={{ color: 'var(--color-text-tertiary)', fontSize: '13px', marginTop: 0 }}>
        A PDF with the totals per site and well and per hauler, then every ticket. Wells on a
        battery are reported as one battery total.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
        <label style={LABEL}>
          Period
          <select
            style={INPUT}
            value={period}
            onChange={(e): void => {
              setPeriod(e.target.value);
              setSiteId('');
            }}
          >
            {months.map((m, i) => (
              <option key={m.from} value={String(i)}>
                {m.label}
              </option>
            ))}
            <option value={CUSTOM}>Choose dates…</option>
          </select>
        </label>
        {period === CUSTOM && (
          <>
            <label style={LABEL}>
              From
              <input
                type="date"
                style={INPUT}
                value={customFrom}
                onChange={(e): void => setCustomFrom(e.target.value)}
              />
            </label>
            <label style={LABEL}>
              To
              <input
                type="date"
                style={INPUT}
                value={customTo}
                onChange={(e): void => setCustomTo(e.target.value)}
              />
            </label>
          </>
        )}
        <label style={LABEL}>
          Site
          <select style={INPUT} value={siteId} onChange={(e): void => setSiteId(e.target.value)}>
            <option value="">All sites</option>
            {siteOptions.map((g) => (
              <option key={g.key} value={g.key}>
                {g.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ ...LABEL, flexDirection: 'row', alignItems: 'center', gap: '6px' }}>
          <input
            type="checkbox"
            checked={runTickets}
            onChange={(e): void => setRunTickets(e.target.checked)}
          />
          Include run tickets
        </label>
        <button
          type="button"
          style={{ ...BUTTON, cursor: statement.isPending ? 'wait' : 'pointer' }}
          disabled={statement.isPending || !rangeOk}
          onClick={(): void => {
            statement.mutate(
              {
                from: chosen.from,
                to: chosen.to,
                timeZone,
                ...(siteId === '' ? {} : { siteId }),
                runTickets,
              },
              {
                onSuccess: (link) => {
                  window.open(link.url, '_blank', 'noopener');
                },
              },
            );
          }}
        >
          {statement.isPending ? 'Making statement…' : 'Download PDF'}
        </button>
      </div>
      {!rangeOk && (
        <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '13px', marginBottom: 0 }}>
          Choose a start date on or before the end date.
        </p>
      )}
      {statement.isError && (
        <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '13px', marginBottom: 0 }}>
          {statementErrorMessage(statement.error)}
        </p>
      )}
    </section>
  );
}
